import * as React from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { TableVirtuoso } from 'react-virtuoso'
import { useEffect, useState } from 'react'
import './TableComponent.css'
import CSVDownloadButton from './CSVDownloadButton'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useApplicationSuiteContext } from '../../../../contexts/ApplicationSuiteProvider'
import LockIcon from '@mui/icons-material/Lock'
import UpgradeDialog from '../../../../components/UpgradeDialog'
import IconButton from '@mui/material/IconButton'
import getNested from 'lodash.get'
import SparklineBar, { CategoryBadge } from '../../../../components/backtests/SparklineBar'
import {
  calculateExpectancyFromTrades,
  estimateExpectancyFromRatio,
} from '../../../../common/utils/rMultipleCalculations'
import { MonteCarloResponse } from '../../../../types/monteCarlo'
import { SummaryWithRankings } from '../../../../hooks/useBacktestRankings'
import ColumnHelpTooltip from '../../../../components/backtests/ColumnHelpTooltip'
import ForkRightIcon from '@mui/icons-material/ForkRight'

interface Data {
  [key: string]: any
}

interface ColumnData {
  dataKey: keyof Data
  label: string
  labelCondensed?: string | null
  showCondensed?: boolean
  numeric?: boolean
  type?: 'string' | 'currency' | 'number' | 'percentage'
  width?: number
  hasProfitColor?: boolean
  showSparkline?: boolean
  isRankColumn?: boolean
  sparklineColor?: string
  group?: 'essentials' | 'advanced' | 'basic' | 'basics' | 'always'
  explanation?: string
}

const columns: ColumnData[] = [
  // Always visible columns
  {
    label: 'Ratio (risk/reward)',
    labelCondensed: 'Risk / Reward',
    dataKey: 'group',
    type: 'string',
    group: 'always',
  },

  // Essentials group
  {
    label: 'Profit %',
    labelCondensed: 'Profit',
    showCondensed: true,
    dataKey: 'profitPercentage',
    type: 'percentage',
    hasProfitColor: true,
    showSparkline: true,
    group: 'essentials',
  },
  {
    label: 'Absolute Drawdown',
    labelCondensed: 'Abs. Drawdown',
    dataKey: 'absoluteDrawdownPercentage',
    type: 'percentage',
    showSparkline: true,
    sparklineColor: '#ff6b6b',
    group: 'essentials',
  },
  {
    label: 'Relative Drawdown',
    labelCondensed: 'Rel. Drawdown',
    dataKey: 'maxRelativeDrawdownPercentage',
    type: 'percentage',
    showSparkline: true,
    sparklineColor: '#ff6b6b',
    group: 'essentials',
  },
  {
    label: 'Win Rate',
    labelCondensed: 'Win Rate',
    dataKey: 'winRate',
    type: 'percentage',
    showSparkline: true,
    group: 'essentials',
  },

  // Basics group
  {
    label: 'Net Profit',
    showCondensed: false,
    dataKey: 'currentProfit',
    type: 'currency',
    group: 'basics',
  },
  {
    label: 'Gross Profit',
    showCondensed: false,
    dataKey: 'grossProfit',
    type: 'currency',
    group: 'basics',
  },
  {
    label: 'Gross Loss',
    showCondensed: false,
    dataKey: 'grossLoss',
    type: 'currency',
    group: 'basics',
  },
  {
    label: 'Total Trades',
    labelCondensed: 'Trades',
    dataKey: 'closedTradeCount',
    type: 'number',
    group: 'basics',
  },
  {
    label: 'Consecutive Wins',
    labelCondensed: 'Consec. Wins',
    dataKey: 'winStreak.streak',
    type: 'number',
    group: 'basics',
  },
  {
    label: 'Consecutive Losses',
    labelCondensed: 'Consec. Losses',
    dataKey: 'lossStreak.streak',
    type: 'number',
    group: 'basics',
  },

  // Advanced group
  {
    label: 'Expectancy (R/trade)',
    labelCondensed: 'Expectancy',
    dataKey: 'expectancy',
    type: 'number',
    showCondensed: true,
    group: 'advanced',
    explanation:
      'Average profit or loss per trade in R-multiples. R-multiples are calculated as price movement divided by stop loss distance. Formula: (Average Win R × Win Rate) + (Average Loss R × Loss Rate). Positive expectancy means the strategy is profitable on average.',
  },
  {
    label: 'Profit Factor',
    labelCondensed: 'P. Factor',
    dataKey: 'profitFactor',
    type: 'number',
    showCondensed: true,
    group: 'advanced',
    explanation:
      'Ratio of gross profit to gross loss. Values above 1.0 indicate a profitable strategy. Higher values suggest better risk-adjusted performance. Formula: Gross Profit ÷ |Gross Loss|.',
  },
  {
    label: 'Percentile Rank',
    labelCondensed: '%ile',
    dataKey: 'monteCarloRobustness',
    type: 'string',
    showCondensed: false,
    group: 'advanced',
    explanation:
      'Shows how the actual backtest performance ranks among 5,000 Monte Carlo simulations. Higher percentiles indicate the strategy performed better than most random scenarios, suggesting more robust results.',
  },
  {
    label: 'Equity Range',
    labelCondensed: 'Equity',
    dataKey: 'monteCarloFinalEquityRange',
    type: 'string',
    showCondensed: false,
    group: 'advanced',
    explanation:
      'Range of final equity values from Monte Carlo analysis (5th to 95th percentile). Shows the expected range of outcomes if the strategy were run multiple times with different trade sequences.',
  },
  {
    label: 'Risk of Ruin %',
    labelCondensed: 'Risk of Ruin',
    dataKey: 'riskOfRuin',
    type: 'percentage',
    showCondensed: false,
    group: 'advanced',
    explanation:
      'Probability of losing a significant portion of your account (typically 50% or more) based on Monte Carlo analysis. Lower percentages indicate safer strategies with less risk of catastrophic losses.',
  },
  {
    label: 'Drawdown Range',
    labelCondensed: 'DD Range',
    dataKey: 'monteCarloDrawdownRange',
    type: 'string',
    showCondensed: false,
    group: 'advanced',
    explanation:
      'Range of maximum drawdowns from Monte Carlo analysis (median to 95th percentile). Shows the expected range of worst-case losses if the strategy were run multiple times with different trade sequences.',
  },
]

const VirtuosoTableComponents: any = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props: any) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead,
  TableRow: ({ item: _item, ...props }: any) => <TableRow {...props} />,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
}

function FixedHeaderContent({
  condensed,
  visibleGroups,
}: {
  condensed: boolean
  visibleGroups: string[]
}) {
  const theme = useTheme()

  const width = condensed ? 70 : 120

  return (
    <TableRow
      sx={{
        background: theme.palette.background.custom2,
      }}
    >
      <TableCell style={{ width: condensed ? 40 : 50 }}></TableCell>
      {columns
        .filter((column) => {
          // Always show 'always' columns (like trade ratio)
          if (column.group === 'always') return true
          // Show other columns based on condensed mode and visible groups
          const showByCondensed = condensed ? column.showCondensed !== false : true
          const showByGroup = !column.group || visibleGroups.includes(column.group)
          return showByCondensed && showByGroup
        })
        .map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.numeric || false ? 'right' : 'left'}
            style={{ width: column.width || width }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: column.numeric ? 'flex-end' : 'flex-start',
              }}
            >
              <span>{condensed ? column.labelCondensed : column.label}</span>
              {column.group === 'advanced' && column.explanation && (
                <ColumnHelpTooltip explanation={column.explanation} condensed={condensed} />
              )}
            </Box>
          </TableCell>
        ))}
    </TableRow>
  )
}

function RowContent(props: {
  backtest: any
  index: number
  row: any
  condensed: boolean
  allRows?: any[]
  visibleGroups?: string[]
}) {
  const { backtest, row, condensed, allRows = [], visibleGroups = [] } = props

  const { usermeta, canShowcaseViewBacktestSettings } = useApplicationSuiteContext()

  const [upgradeDialogOpen, setUpgradeDialogOpen] = React.useState(false)
  const [hoveringLock, setHoveringLock] = React.useState(false)

  function traderOwnsBacktest() {
    return backtest.gcid === usermeta.gcid
  }

  const locked = !canShowcaseViewBacktestSettings && !traderOwnsBacktest()

  // Helper function to get min/max values for sparklines
  function getMinMaxForColumn(column: ColumnData) {
    if (!allRows.length) return { min: 0, max: 100 }

    const values = allRows.map((r) => {
      let value = getNested(r, column.dataKey)
      if (column.dataKey === 'winRate') {
        if (r['closedTradeCount']) {
          value = (r['profitTradeCount'] / r['closedTradeCount']) * 100
        } else {
          value = 0
        }
      }
      return parseFloat(value) || 0
    })

    const maxValue = Math.max(...values)
    const minValue = Math.min(...values)

    // Handle negative values by using the actual min when there are negatives
    // Otherwise use 0 as minimum for better visual scaling
    const finalMin = minValue < 0 ? minValue : 0

    return {
      min: finalMin,
      max: maxValue,
    }
  }

  function gatherData(row: any, column: ColumnData) {
    let value = getNested(row, column.dataKey)
    const type = column.type

    if (column.dataKey === 'winRate') {
      if (row['closedTradeCount']) {
        value = (row['profitTradeCount'] / row['closedTradeCount']) * 100
      } else {
        value = 0
      }
    }

    // Custom formatting for new columns
    if (column.dataKey === 'expectancy') {
      return value ? `${value.toFixed(2)}R` : '-'
    } else if (column.dataKey === 'profitFactor') {
      if (value >= 999) return '∞'
      return value ? value.toFixed(2) : '-'
    } else if (column.dataKey === 'monteCarloRobustness') {
      return value || '-'
    } else if (column.dataKey === 'monteCarloFinalEquityRange') {
      return value || '-'
    } else if (column.dataKey === 'riskOfRuin') {
      return value !== null && value !== undefined ? `${(value * 100).toFixed(1)}%` : '-'
    } else if (column.dataKey === 'monteCarloDrawdownRange') {
      return value || '-'
    }

    return formatData(value, type)
  }

  function formatData(data: any, type?: 'string' | 'currency' | 'number' | 'percentage') {
    if (type === 'currency') {
      return `$${parseFloat(data).toFixed(2)}`
    } else if (type === 'number') {
      return data
    } else if (type === 'percentage') {
      // if 3 digit number or more, round to 0 decimal place
      // if 2 digit number, round to 1 decimal place
      // if 1 digit number, round to 2 decimal places

      const num = parseInt(data)
      const numStr = num.toString()

      if (numStr.length >= 3) {
        return `${parseFloat(data).toFixed(0)}%`
      } else if (numStr.length === 2) {
        return `${parseFloat(data).toFixed(1)}%`
      } else if (numStr.length === 1) {
        return `${parseFloat(data).toFixed(2)}%`
      }

      return `${parseFloat(data).toFixed(0)}%`
    } else {
      return data
    }
  }

  return (
    <React.Fragment>
      <TableCell sx={{ textAlign: 'center' }}>
        {locked ? (
          <IconButton
            onClick={() => setUpgradeDialogOpen(true)}
            onMouseEnter={() => setHoveringLock(true)}
            onMouseLeave={() => setHoveringLock(false)}
            size={'small'}
          >
            <LockIcon
              sx={{ color: hoveringLock || upgradeDialogOpen ? '#ffa726' : 'lightgray' }}
              fontSize={'small'}
            />
          </IconButton>
        ) : (
          <CSVDownloadButton backtest={backtest} group={row.group} />
        )}
      </TableCell>
      {columns
        .filter((column) => {
          // Always show 'always' columns (like trade ratio)
          if (column.group === 'always') return true
          // Show other columns based on condensed mode and visible groups
          const showByCondensed = condensed ? column.showCondensed !== false : true
          const showByGroup = !column.group || visibleGroups.includes(column.group)
          return showByCondensed && showByGroup
        })
        .map((column) => {
          const value = getNested(row, column.dataKey)
          let displayValue = gatherData(row, column)

          // Handle sparkline columns
          if (column.showSparkline && !condensed) {
            const { min, max } = getMinMaxForColumn(column)
            let sparklineValue = parseFloat(value) || 0

            // Calculate win rate for sparkline
            if (column.dataKey === 'winRate') {
              if (row['closedTradeCount']) {
                sparklineValue = (row['profitTradeCount'] / row['closedTradeCount']) * 100
              } else {
                sparklineValue = 0
              }
            }

            return (
              <TableCell
                sx={{
                  py: condensed ? 0 : 1,
                  p: 1,
                  color: column.hasProfitColor ? 'rgb(46, 240, 50)' : 'inherit',
                }}
                key={column.dataKey}
                align={column.numeric || false ? 'right' : 'left'}
              >
                <SparklineBar
                  value={sparklineValue}
                  maxValue={max}
                  minValue={min}
                  width={80}
                  height={20}
                  color={column.sparklineColor}
                  formatValue={(val: number) => {
                    if (column.type === 'percentage') {
                      return `${val.toFixed(1)}%`
                    } else if (column.type === 'currency') {
                      return `$${Math.abs(val).toFixed(0)}`
                    }
                    return val.toFixed(1)
                  }}
                />
              </TableCell>
            )
          }

          // Special handling for group column to show rank badges and conditional ratio info
          if (column.dataKey === 'group') {
            const tradeRatios = backtest.backtestSettings?.generateTrades?.tradeRatios || []
            const parentRatio = tradeRatios.find((r: any) => `${r.risk}/${r.reward}` === row.group)
            const hasConditions = parentRatio?.conditions?.length > 0
            const conditionCount = parentRatio?.conditions?.length || 0

            return (
              <TableCell
                sx={{
                  py: condensed ? 0 : 1,
                  p: 0,
                  color: column.hasProfitColor ? 'rgb(46, 240, 50)' : 'inherit',
                }}
                key={column.dataKey}
                align={column.numeric || false ? 'right' : 'left'}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2">{displayValue}</Typography>
                  {hasConditions && (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">
                        · {conditionCount}
                      </Typography>
                      <ForkRightIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </Box>
                  )}
                  {row.rankings && row.rankings.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      {row.rankings.map((ranking: any, index: number) => (
                        <CategoryBadge
                          key={`${ranking.category}-${index}`}
                          category={ranking.category}
                          badge={ranking.badge}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </TableCell>
            )
          }

          // Regular cell
          return (
            <TableCell
              sx={{
                py: condensed ? 0 : 1,
                p: 0,
                color: column.hasProfitColor ? 'rgb(46, 240, 50)' : 'inherit',
              }}
              key={column.dataKey}
              align={column.numeric || false ? 'right' : 'left'}
            >
              {displayValue}
            </TableCell>
          )
        })}
      {upgradeDialogOpen && (
        <UpgradeDialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} />
      )}
    </React.Fragment>
  )
}

export default function ReactVirtualizedTable(props: {
  backtest: any
  condensed?: any
  tradesByGroup?: { [group: string]: any[] }
  monteCarloResults?: { [group: string]: MonteCarloResponse }
  visibleGroups?: string[]
  summariesWithRankings?: SummaryWithRankings[]
}) {
  const {
    backtest,
    condensed,
    tradesByGroup,
    monteCarloResults,
    visibleGroups = ['essentials', 'basics'],
    summariesWithRankings,
  } = props

  const theme = useTheme()

  const [rows, setRows] = useState<any>([])
  const [scrollState, setScrollState] = useState({ canScrollLeft: false, canScrollRight: true })

  const handleScroll = (event: any) => {
    const { scrollLeft, scrollWidth, clientWidth } = event.target
    setScrollState({
      canScrollLeft: scrollLeft > 0,
      canScrollRight: scrollLeft < scrollWidth - clientWidth,
    })
  }

  useEffect(() => {
    // Use summaries with rankings if available, otherwise fall back to original backtest.summaries
    const summariesToProcess = summariesWithRankings || backtest.summaries

    if (summariesToProcess) {
      const processedSummaries = summariesToProcess.map((summary: any) => {
        // Calculate Expectancy using proper R-multiple formula
        let expectancy = 0
        if (summary.closedTradeCount > 0) {
          // Use individual trades for accurate R-multiple calculation if available
          if (tradesByGroup && tradesByGroup[summary.group]) {
            const trades = tradesByGroup[summary.group]
            expectancy = calculateExpectancyFromTrades(trades)
          } else {
            // Fallback: estimate from ratio string (e.g., "1/6" → winners = +6R, losers = -1R)
            expectancy = estimateExpectancyFromRatio(
              summary.group,
              summary.profitTradeCount,
              summary.closedTradeCount
            )
          }
        }

        // Calculate Profit Factor
        const profitFactor =
          summary.grossLoss && summary.grossLoss !== 0
            ? (summary.grossProfit || 0) / Math.abs(summary.grossLoss)
            : summary.grossProfit > 0
              ? 999
              : 0

        // Calculate Monte Carlo Robustness using actual MC data
        let monteCarloRobustness = `-`
        let monteCarloFinalEquityRange = '-'
        let riskOfRuin: number | null = null
        let monteCarloDrawdownRange = '-'
        if (monteCarloResults && monteCarloResults[summary.group]) {
          const mcData = monteCarloResults[summary.group]
          // Use baseline percentile among MC simulations as robustness metric
          const percentile = mcData.baseline?.percentileAmongMC || 0
          monteCarloRobustness = `${percentile.toFixed(0)}%ile`

          // Final Equity Range (p05-p95)
          const finals = mcData.aggregates?.finals?.quantiles
          if (finals && finals['0.05'] !== undefined && finals['0.95'] !== undefined) {
            const p05 = finals['0.05']
            const p95 = finals['0.95']
            const formatEquity = (value: number) => {
              if (value >= 1000) {
                return `$${(value / 1000).toFixed(1)}k`
              } else {
                return `$${value.toFixed(0)}`
              }
            }
            monteCarloFinalEquityRange = `${formatEquity(p05)} – ${formatEquity(p95)}`
          }

          // Risk of Ruin %
          riskOfRuin = mcData.aggregates?.riskOfRuin?.probability ?? 0

          // MC Drawdown Range (median vs worst-case)
          const maxDD = mcData.aggregates?.maxDrawdown?.quantiles
          if (maxDD && maxDD['0.5'] !== undefined && maxDD['0.95'] !== undefined) {
            const medianDD = maxDD['0.5']
            const worstDD = maxDD['0.95']
            monteCarloDrawdownRange = `${medianDD.toFixed(1)}% – ${worstDD.toFixed(1)}%`
          }
        }

        return {
          ...summary,
          expectancy,
          profitFactor,
          monteCarloRobustness,
          monteCarloFinalEquityRange,
          riskOfRuin,
          monteCarloDrawdownRange,
        }
      })

      processedSummaries.sort((a: any, b: any) => {
        return parseFloat(a.currentProfit || Number.MIN_SAFE_INTEGER) -
          parseFloat(b.currentProfit || Number.MIN_SAFE_INTEGER) >
          0
          ? -1
          : 1
      })
      setRows(processedSummaries)
      return
    }
  }, [backtest, monteCarloResults, summariesWithRankings, tradesByGroup])

  // Compute the height based on the number of rows (capped at maxHeight)
  const headerHeight = condensed ? 32 : 40
  const rowHeight = condensed ? 40 : 50
  const maxHeight = 300
  const computedHeight = Math.min(headerHeight + rows.length * rowHeight + rowHeight / 2, maxHeight)

  return (
    <Paper
      elevation={0}
      style={{ height: computedHeight, width: '100%', position: 'relative', background: 'none' }}
    >
      <Box
        className={`table-fade ${!scrollState.canScrollLeft ? 'no-left-fade' : ''} ${!scrollState.canScrollRight ? 'no-right-fade' : ''}`}
        sx={{
          '&:before': {
            left: 0,
            background: `linear-gradient(to right, ${theme.palette.background.custom3}, rgba(255, 255, 255, 0))`,
          },
          '&:after': {
            right: 0,
            background: `linear-gradient(to left, ${theme.palette.background.custom3}, rgba(255, 255, 255, 0))`,
          },
          '& .MuiTableContainer-root': {},
          '& .MuiTable-root': {
            borderSpacing: '0 2px !important',
          },
          '& .MuiTableCell-root': {
            padding: `${condensed ? 0 : 4}px 1px`,
          },
        }}
      >
        <TableVirtuoso
          style={{
            height: computedHeight,
            width: '100%',
            background: theme.palette.background.custom2,
          }}
          data={rows}
          components={VirtuosoTableComponents}
          fixedHeaderContent={() => (
            <FixedHeaderContent condensed={condensed} visibleGroups={visibleGroups} />
          )}
          itemContent={(index, row) => (
            <RowContent
              backtest={backtest}
              index={index}
              row={row}
              condensed={condensed}
              allRows={rows}
              visibleGroups={visibleGroups}
            />
          )}
          onScroll={handleScroll}
        />
      </Box>
    </Paper>
  )
}

function RowContentSmall(props: {
  backtest: any
  index: number
  row: any
  condensed: boolean
  allRows?: any[]
}) {
  const { backtest, row, condensed } = props

  const { usermeta, canShowcaseViewBacktestSettings } = useApplicationSuiteContext()
  const [upgradeDialogOpen, setUpgradeDialogOpen] = React.useState(false)
  const [hoveringLock, setHoveringLock] = React.useState(false)

  function traderOwnsBacktest() {
    return backtest.gcid === usermeta.gcid
  }

  const locked = !canShowcaseViewBacktestSettings && !traderOwnsBacktest()
  const cellWidth = condensed ? 70 : 120

  return (
    <React.Fragment>
      {/* Download column cell */}
      <TableCell sx={{ width: condensed ? 40 : 50, textAlign: 'center' }} align="left">
        {locked ? (
          <IconButton
            onClick={() => setUpgradeDialogOpen(true)}
            onMouseEnter={() => setHoveringLock(true)}
            onMouseLeave={() => setHoveringLock(false)}
            size="small"
          >
            <LockIcon
              sx={{ color: hoveringLock || upgradeDialogOpen ? '#ffa726' : 'lightgray' }}
              fontSize="small"
            />
          </IconButton>
        ) : (
          <CSVDownloadButton backtest={backtest} group={row.group} />
        )}
      </TableCell>
      {/* Trade Ratio column cell */}
      <TableCell
        sx={{
          py: condensed ? 0 : 1,
          px: condensed ? 0 : 1,
          width: cellWidth,
        }}
        align="left"
      >
        {(() => {
          const tradeRatios = backtest.backtestSettings?.generateTrades?.tradeRatios || []
          const parentRatio = tradeRatios.find((r: any) => `${r.risk}/${r.reward}` === row.group)
          const hasConditions = parentRatio?.conditions?.length > 0
          const conditionCount = parentRatio?.conditions?.length || 0
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>{row.group || '-'}</span>
              {hasConditions && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    · {conditionCount}
                  </Typography>
                  <ForkRightIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </Box>
              )}
            </Box>
          )
        })()}
      </TableCell>
      {/* Profit column cell */}
      <TableCell
        sx={{
          color: row.currentProfit > 0 ? 'rgb(46, 240, 50)' : 'inherit',
          py: condensed ? 0 : 1,
          px: condensed ? 0 : 1,
          width: cellWidth,
        }}
        align="left"
      >
        {row.profitPercentage ? `${parseFloat(row.profitPercentage).toFixed(0)}%` : '-'}
      </TableCell>
      {upgradeDialogOpen && (
        <UpgradeDialog open={upgradeDialogOpen} onClose={() => setUpgradeDialogOpen(false)} />
      )}
    </React.Fragment>
  )
}

function SimpleTableHeader({ condensed }: { condensed: boolean }) {
  const cellWidth = condensed ? 70 : 120
  return (
    <TableRow>
      {/* Download column header (empty, fixed width) */}
      <TableCell
        sx={{ py: condensed ? 0 : 1, px: condensed ? 0 : 1, width: condensed ? 40 : 50 }}
        align="left"
      />
      {/* Trade Ratio column header */}
      <TableCell
        sx={{ py: condensed ? 0 : 1, px: condensed ? 0 : 1, width: cellWidth }}
        align="left"
      >
        {condensed ? 'Risk / Reward' : 'Ratio (risk/reward)'}
      </TableCell>
      {/* Profit column header */}
      <TableCell
        sx={{ py: condensed ? 0 : 1, px: condensed ? 0 : 1, width: cellWidth }}
        align="left"
      >
        Profit %
      </TableCell>
    </TableRow>
  )
}

export function SimpleReportTable(props: {
  backtest: any
  condensed?: boolean
  tradesByGroup?: { [group: string]: any[] }
  monteCarloResults?: { [group: string]: MonteCarloResponse }
  visibleGroups?: string[]
}) {
  const { backtest, condensed, tradesByGroup, monteCarloResults } = props
  const [rows, setRows] = useState<any[]>([])

  useEffect(() => {
    if (backtest.summaries) {
      const processedSummaries = backtest.summaries.map((summary: any) => {
        // Calculate Expectancy using proper R-multiple formula
        let expectancy = 0
        if (summary.closedTradeCount > 0) {
          // Use individual trades for accurate R-multiple calculation if available
          if (tradesByGroup && tradesByGroup[summary.group]) {
            const trades = tradesByGroup[summary.group]
            expectancy = calculateExpectancyFromTrades(trades)
          } else {
            // Fallback: estimate from ratio string (e.g., "1/6" → winners = +6R, losers = -1R)
            expectancy = estimateExpectancyFromRatio(
              summary.group,
              summary.profitTradeCount,
              summary.closedTradeCount
            )
          }
        }

        // Calculate Profit Factor
        const profitFactor =
          summary.grossLoss && summary.grossLoss !== 0
            ? (summary.grossProfit || 0) / Math.abs(summary.grossLoss)
            : summary.grossProfit > 0
              ? 999
              : 0

        // Calculate Monte Carlo Robustness using actual MC data
        let monteCarloRobustness = `-`
        let monteCarloFinalEquityRange = '-'
        let riskOfRuin: number | null = null
        let monteCarloDrawdownRange = '-'
        if (monteCarloResults && monteCarloResults[summary.group]) {
          const mcData = monteCarloResults[summary.group]
          // Use baseline percentile among MC simulations as robustness metric
          const percentile = mcData.baseline?.percentileAmongMC || 0
          monteCarloRobustness = `${percentile.toFixed(0)}%ile`

          // Final Equity Range (p05-p95)
          const finals = mcData.aggregates?.finals?.quantiles
          if (finals && finals['0.05'] !== undefined && finals['0.95'] !== undefined) {
            const p05 = finals['0.05']
            const p95 = finals['0.95']
            const formatEquity = (value: number) => {
              if (value >= 1000) {
                return `$${(value / 1000).toFixed(1)}k`
              } else {
                return `$${value.toFixed(0)}`
              }
            }
            monteCarloFinalEquityRange = `${formatEquity(p05)} – ${formatEquity(p95)}`
          }

          // Risk of Ruin %
          riskOfRuin = mcData.aggregates?.riskOfRuin?.probability ?? 0

          // MC Drawdown Range (median vs worst-case)
          const maxDD = mcData.aggregates?.maxDrawdown?.quantiles
          if (maxDD && maxDD['0.5'] !== undefined && maxDD['0.95'] !== undefined) {
            const medianDD = maxDD['0.5']
            const worstDD = maxDD['0.95']
            monteCarloDrawdownRange = `${medianDD.toFixed(1)}% – ${worstDD.toFixed(1)}%`
          }
        }

        return {
          ...summary,
          expectancy,
          profitFactor,
          monteCarloRobustness,
          monteCarloFinalEquityRange,
          riskOfRuin,
          monteCarloDrawdownRange,
        }
      })

      // Sort descending by currentProfit (using Number.MIN_SAFE_INTEGER as fallback)
      const sorted = processedSummaries.sort((a: any, b: any) => {
        const profitA = parseFloat(a.currentProfit || Number.MIN_SAFE_INTEGER)
        const profitB = parseFloat(b.currentProfit || Number.MIN_SAFE_INTEGER)
        return profitB - profitA
      })
      setRows(sorted)
    }
  }, [backtest, tradesByGroup, monteCarloResults])

  // Compute the height based on the number of rows (capped at maxHeight)
  const headerHeight = condensed ? 32 : 40
  const rowHeight = condensed ? 40 : 50
  const computedHeight = Math.min(headerHeight + rows.length * rowHeight)

  return (
    <Paper elevation={0} style={{ width: '100%', position: 'relative', background: 'none' }}>
      <Box
        sx={{
          overflowX: 'hidden',
          '& .MuiTable-root': {
            borderSpacing: '0 2px !important',
          },
          '& .MuiTableCell-root': {
            padding: `${condensed ? 0 : 4}px 1px`,
          },
        }}
      >
        <TableVirtuoso
          style={{ height: computedHeight, width: '100%', background: 'none' }}
          data={rows}
          components={VirtuosoTableComponents}
          fixedHeaderContent={() => <SimpleTableHeader condensed={!!condensed} />}
          itemContent={(index, row) => (
            <RowContentSmall
              backtest={backtest}
              index={index}
              row={row}
              condensed={!!condensed}
              allRows={rows}
            />
          )}
        />
      </Box>
    </Paper>
  )
}
